'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import {
  tl,
  ol,
  sl,
  cl,
  rl,
  nl,
  il,
  al,
  CollectionStories,
} from './collection-stories';
import { ArtifactStatus, bs } from './playback-controls';
import {
  CollectionReveal,
  V,
  Scene_js,
  Scene_ks,
  Ks,
} from './collection-artifact-primitives';
import { ShowcaseIcon } from './scene-primitives';
import { collectionFacts } from './workflow-facts';
import { ec, ProductionBriefs } from './production-briefs';
import { CollectionDirection, fc } from './collection-direction';
import { CollectionLinePlan, zc } from './collection-line-plan';
import { Zc, CollectionMaterials } from './collection-materials';
export function TrendResearch({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = Math.round(
    tl.sourceCount * Math.min(1, Math.max(0, (e - ol(0)) / (sl + 400 - ol(0))))
  );
  return (
    <div className={'flex h-full flex-col'}>
      <ArtifactStatus
        icon={'doc'}
        title={translateShowcaseNode(showcaseCopy, tl.title)}
        done={e >= cl}
        working={'Researching'}
        doneLabel={'Report ready'}
      />
      <div
        className={
          'grid flex-1 gap-5 p-4 sm:p-5 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] md:gap-8 md:p-6'
        }
      >
        <div className={'min-w-0'}>
          <CollectionReveal on={e >= rl} blur={2} y={4}>
            <p className={'label'}>
              {translateShowcaseNode(showcaseCopy, 'Recommendation')}
            </p>
            <h3
              className={
                'text-ink mt-1 text-[16px] leading-[22px] font-semibold tracking-[-0.01em] text-pretty'
              }
            >
              {translateShowcaseNode(showcaseCopy, tl.decision)}
            </h3>
            <p
              className={
                'text-ink-3 mt-2 flex items-start gap-2 text-[12.5px] leading-[18px]'
              }
            >
              <span
                className={
                  'text-ink-2 inline-flex h-5 shrink-0 items-center rounded-[6px] px-1.5 text-[11px] font-medium shadow-[inset_0_0_0_1px_var(--line-strong)]'
                }
              >
                {translateShowcaseNode(showcaseCopy, 'Confidence: ')}
                {translateShowcaseNode(showcaseCopy, tl.confidence)}
              </span>
              <span className={'text-pretty max-sm:hidden'}>
                {translateShowcaseNode(showcaseCopy, tl.confidenceWhy)}
              </span>
            </p>
          </CollectionReveal>
          <ol className={'mt-4 flex flex-col gap-2.5'}>
            {translateShowcaseNode(
              showcaseCopy,
              tl.signals.map((t, n) => {
                let r = V[nl[t.label] ?? 0];
                return (
                  <CollectionReveal
                    key={t.label}
                    as={'li'}
                    on={e >= il(n)}
                    className={'flex items-start gap-3'}
                  >
                    {translateShowcaseNode(
                      showcaseCopy,
                      r ? (
                        <Scene_js
                          piece={r}
                          className={'size-10 shrink-0 rounded-[8px]'}
                        />
                      ) : null
                    )}
                    <span className={'min-w-0 flex-1'}>
                      <span
                        className={
                          'text-ink block text-[13.5px] leading-5 font-medium'
                        }
                      >
                        {translateShowcaseNode(showcaseCopy, t.label)}
                      </span>
                      <span
                        className={
                          'text-ink-3 block text-[12.5px] leading-[18px] text-pretty'
                        }
                      >
                        {translateShowcaseNode(showcaseCopy, t.finding)}
                      </span>
                    </span>
                  </CollectionReveal>
                );
              })
            )}
          </ol>
        </div>
        <div className={'flex min-w-0 flex-col gap-5'}>
          <div>
            <Scene_ks>
              {translateShowcaseNode(
                showcaseCopy,
                'Palette, with Oxblood as the one accent'
              )}
            </Scene_ks>
            <div
              className={
                'mt-2 flex h-11 overflow-hidden rounded-[10px] shadow-[inset_0_0_0_1px_var(--img-outline)] md:h-12'
              }
            >
              {translateShowcaseNode(
                showcaseCopy,
                bs.map((t, n) => (
                  <span
                    key={t.name}
                    className={
                      'h-full flex-1 origin-bottom transition-[transform,opacity] duration-[520ms] ease-[var(--ease)]'
                    }
                    style={{
                      backgroundColor: t.hex,
                      transform: e >= al + n * 90 ? 'none' : 'scaleY(0)',
                      opacity: +(e >= al + n * 90),
                    }}
                  />
                ))
              )}
            </div>
            <div className={'mt-1.5 flex'}>
              {translateShowcaseNode(
                showcaseCopy,
                bs.map((t, n) => (
                  <CollectionReveal
                    key={t.name}
                    as={'span'}
                    on={e >= 2800 + n * 90}
                    y={2}
                    className={
                      'text-ink-3 flex-1 truncate text-[11px] leading-4'
                    }
                  >
                    {translateShowcaseNode(showcaseCopy, t.name)}
                  </CollectionReveal>
                ))
              )}
            </div>
          </div>
          <div className={'min-w-0'}>
            <Scene_ks className={'flex items-center gap-1.5'}>
              {translateShowcaseNode(showcaseCopy, 'Sources')}
              <span className={'num text-ink'}>
                {translateShowcaseNode(showcaseCopy, t)}
              </span>
              <span className={'text-ink-4'}>
                {translateShowcaseNode(showcaseCopy, 'cited')}
              </span>
            </Scene_ks>
            <ul className={'mt-1.5 flex flex-col'}>
              {translateShowcaseNode(
                showcaseCopy,
                tl.sources.map((t, n) => (
                  <CollectionReveal
                    key={t.publisher}
                    as={'li'}
                    on={e >= ol(n)}
                    y={3}
                    className={
                      'flex min-w-0 items-start gap-2 py-1 text-[12.5px] leading-4'
                    }
                  >
                    <ShowcaseIcon
                      name={'link'}
                      className={'text-ink-4 mt-px size-3.5 shrink-0'}
                    />
                    <span className={'min-w-0 flex-1'}>
                      <span className={'flex items-baseline gap-2'}>
                        <span className={'text-ink truncate font-medium'}>
                          {translateShowcaseNode(showcaseCopy, t.publisher)}
                        </span>
                        <span
                          className={
                            'num text-ink-4 ml-auto shrink-0 text-[12px]'
                          }
                        >
                          {translateShowcaseNode(showcaseCopy, t.date)}
                        </span>
                      </span>
                      <span
                        className={
                          'text-ink-3 block truncate text-[12px] max-sm:hidden'
                        }
                      >
                        {translateShowcaseNode(showcaseCopy, t.title)}
                      </span>
                    </span>
                  </CollectionReveal>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
export function ul(e, t) {
  return {
    name: t,
    does:
      collectionFacts.stages
        .find((t) => t.key === e)
        ?.workflows.find((e) => e.name === t)?.does ?? '',
  };
}
export const dl = collectionFacts.stages
  .filter((e) => e.key === 'plan' || e.key === 'develop')
  .map((e) => ({
    value: e.key === 'plan' ? 'plan' : 'develop',
    label: e.label,
  }));
export const collectionSteps = [
  {
    ...ul('plan', 'Forecast Trends'),
    key: 'trends',
    stage: 'plan',
    short: 'Trends',
    icon: 'search',
    file: {
      name: 'FW27 trend forecast',
      kind: 'Report',
      icon: 'doc',
    },
    ms: cl,
    Artifact: TrendResearch,
  },
  {
    ...ul('plan', 'Set Collection Direction'),
    key: 'direction',
    stage: 'plan',
    short: 'Direction',
    icon: 'compass',
    file: {
      name: 'FW27 direction',
      kind: 'Document',
      icon: 'doc',
    },
    ms: ec,
    Artifact: CollectionDirection,
  },
  {
    ...ul('plan', 'Build a Line Plan'),
    key: 'line-plan',
    stage: 'plan',
    short: 'Line plan',
    icon: 'sheet',
    file: {
      name: 'FW27 line plan',
      kind: 'Sheet',
      icon: 'sheet',
    },
    ms: fc,
    Artifact: CollectionLinePlan,
  },
  {
    ...ul('develop', 'Create Product Stories'),
    key: 'stories',
    stage: 'develop',
    short: 'Stories',
    icon: 'doc',
    file: {
      name: 'FW27 product stories',
      kind: 'Document',
      icon: 'doc',
    },
    ms: Zc,
    Artifact: CollectionStories,
  },
  {
    ...ul('develop', 'Plan Colors and Materials'),
    key: 'materials',
    stage: 'develop',
    short: 'Materials',
    icon: 'swatch',
    file: {
      name: 'FW27 colour and materials',
      kind: 'Board',
      icon: 'image',
    },
    ms: zc,
    Artifact: CollectionMaterials,
  },
  {
    ...ul('develop', 'Create Production Briefs'),
    key: 'briefs',
    stage: 'develop',
    short: 'Briefs',
    icon: 'clipboard',
    file: {
      name: 'FW27 production briefs',
      kind: '6 briefs',
      icon: 'folder',
    },
    ms: Ks,
    Artifact: ProductionBriefs,
  },
];
export const pl = ul('plan', 'Create a Collection');
export function CollectionStepButton({ state: e, icon: t, progress: n }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <span
      className={`relative grid size-[30px] shrink-0 place-items-center rounded-full transition-[background-color,color,box-shadow] duration-200 ${e === 'active' ? 'text-ink bg-[var(--panel)]' : e === 'done' ? 'text-ink bg-[var(--state-wash)]' : 'text-ink-3 shadow-[inset_0_0_0_1px_var(--border)]'}`}
    >
      {translateShowcaseNode(
        showcaseCopy,
        e === 'done' ? (
          <ShowcaseIcon
            key={'done'}
            name={'check'}
            draw={'now'}
            className={'size-4'}
          />
        ) : (
          <ShowcaseIcon name={t} className={'size-4'} />
        )
      )}
      {translateShowcaseNode(
        showcaseCopy,
        e === 'active' ? (
          <svg
            viewBox={'0 0 30 30'}
            aria-hidden={!0}
            className={'absolute inset-0 size-full -rotate-90'}
          >
            <circle
              cx={'15'}
              cy={'15'}
              r={'14.25'}
              fill={'none'}
              stroke={'var(--border)'}
              strokeWidth={'1.5'}
            />
            <circle
              cx={'15'}
              cy={'15'}
              r={'14.25'}
              fill={'none'}
              stroke={'var(--foreground)'}
              strokeWidth={'1.5'}
              strokeLinecap={'round'}
              pathLength={1}
              strokeDasharray={'1 2'}
              strokeDashoffset={1 - Math.min(1, Math.max(0, n))}
            />
          </svg>
        ) : null
      )}
    </span>
  );
}
